import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IllustrativeIcon, Txt } from '@vst/beam';
import { Alert, RoadMap } from '@vst/beam-icons/illustrative-icons';
import { useTranslation } from 'react-i18next';

import { CustomDecorativeModal, LoadingModal } from '@app/components/Modal';
import { clearError, selectError } from '@app/redux/error';
import {
  EditableCardType,
  resetScrubbedAddress,
  ScrubAddressModalType,
  selectScrubbedAddress,
} from '@app/redux/scrubbedAddress';
import { selectUserInfo } from '@app/redux/userInfo';

import { AddressInput, ContactMethodLabels } from '@mfe/shared/schema-types';

import { getCountryCode, updateAddressRedux } from '../utils';
import { CommonProfileProps, ProfileTab } from '../types';

import styles from './styles.module.scss';

interface ProfileModalProps extends CommonProfileProps {
  pendingTab: ProfileTab | null;
  setPendingTab: (value: ProfileTab | null) => void;
  setCurrentTab: (value: ProfileTab) => void;
}

const ProfileModal = ({
  setShowModal,
  setPendingTab,
  setCloseAfterReload,
  setEditingCard,
  setRestrictSwitchTabs,
  pendingTab,
  setCurrentTab,
}: ProfileModalProps): JSX.Element | null => {
  const { t } = useTranslation(['Profile', 'Error']);
  const dispatch = useDispatch();

  const {
    userInfo: { accountType },
  } = useSelector(selectUserInfo);
  const { error: scrubbedAddressError } = useSelector(selectError);
  const { scrubbedAddress, rawInput, modalType, editingCard, loading } = useSelector(selectScrubbedAddress);

  const [addressTimeout, setAddressTimeout] = useState<boolean>(false);

  const cancel = () => {
    setShowModal(null);
    setPendingTab(null);
    dispatch(resetScrubbedAddress());
    setAddressTimeout(false);
  };

  const resetEditableCard = () => {
    setShowModal(null);
    setCloseAfterReload(true);
    setEditingCard(EditableCardType.None);
    setRestrictSwitchTabs(false);
    pendingTab && setCurrentTab(pendingTab);
  };

  const saveAddress = () => {
    const newScrubbedAddress = scrubbedAddress.rawAddress;
    const newSecondAddressLine = rawInput.addressLines[1];
    const newCountryCode = newScrubbedAddress.country ? getCountryCode(newScrubbedAddress.country) : 'US';

    const newAddress: AddressInput = {
      addressLines: [newScrubbedAddress.addressLines[0], newSecondAddressLine],
      countryCode: newCountryCode ?? '',
      municipality: newScrubbedAddress.municipality,
      postalCode: newScrubbedAddress?.postalCode ?? '',
      region: newScrubbedAddress?.region ?? '',
      labels: editingCard === EditableCardType.Billing ? [ContactMethodLabels.Invoice] : [ContactMethodLabels.Shipping],
    };
    updateAddressRedux(accountType, dispatch, [newAddress]);
    setEditingCard(EditableCardType.None);
    setRestrictSwitchTabs(false);
    setCloseAfterReload(true);
  };

  if (scrubbedAddressError) {
    setAddressTimeout(scrubbedAddressError.operation === 'getScrubbedAddress');
    dispatch(clearError({}));
  }

  const handleClick =
    (...handlers: Array<(event: React.MouseEvent<HTMLElement>) => void>) =>
    (event: React.MouseEvent<HTMLElement>) => {
      for (const handler of handlers) {
        handler(event);
      }
      event.preventDefault();
    };

  const resetAddressOnTimeout = () => {
    cancel();
    resetScrubbedAddress();
  };

  if (addressTimeout) {
    return (
      <CustomDecorativeModal
        surfaceProps={{ className: styles['actionlessModal'] }}
        icon={<IllustrativeIcon icon={Alert} color="teal_600" />}
        isOpen={addressTimeout}
        actions={{ primary: { label: '', style: { display: 'none' } } }}
        onClose={resetAddressOnTimeout}
        header={t('Error:title')}
      >
        <Txt>{t('Error:modal')}</Txt>
      </CustomDecorativeModal>
    );
  }

  switch (modalType) {
    case ScrubAddressModalType.Unsaved:
      return (
        <CustomDecorativeModal
          header={t('title_UnsavedChanges')}
          icon={<IllustrativeIcon icon={Alert} color="teal_600" />}
          isOpen={modalType === ScrubAddressModalType.Unsaved}
          onClose={cancel}
          actions={{
            primary: {
              label: t('continueEditing'),
              onClick: handleClick(cancel),
            },
            secondary: {
              label: t('discardChanges'),
              onClick: handleClick(resetEditableCard),
            },
          }}
        >
          <Txt>{t('caption_UnsavedChanges')}</Txt>
        </CustomDecorativeModal>
      );

    case ScrubAddressModalType.Address:
      return loading ? (
        <LoadingModal description={t('scrubAddressLoading')} isOpen={modalType === ScrubAddressModalType.Address} />
      ) : (
        <CustomDecorativeModal
          header={t('title_AddressValidation')}
          icon={<IllustrativeIcon icon={RoadMap} color="teal_600" />}
          isOpen={modalType === ScrubAddressModalType.Address}
          onClose={cancel}
          actions={{
            secondary: { label: t('editAddress'), onClick: cancel },
            primary: {
              label: t('useAddress'),
              onClick: handleClick(saveAddress, cancel),
            },
          }}
        >
          <div>
            <Txt>{t('caption_AddressValidation')}</Txt>
            {scrubbedAddress?.formattedAddress && (
              <Txt component="span" variant="bodyLargeBold" color="regular">
                {scrubbedAddress.formattedAddress}
              </Txt>
            )}
          </div>
        </CustomDecorativeModal>
      );

    case ScrubAddressModalType.AddressError:
      return (
        <CustomDecorativeModal
          header={t('title_AddressError')}
          icon={<IllustrativeIcon icon={Alert} color="teal_600" />}
          isOpen={modalType === ScrubAddressModalType.AddressError}
          actions={{
            primary: {
              label: t('close'),
              onClick: handleClick(cancel),
            },
          }}
          onClose={cancel}
        >
          <Txt>{t('caption_AddressError')}</Txt>
        </CustomDecorativeModal>
      );

    default:
      return null;
  }
};

export default ProfileModal;
